/**
 *
 * AbsenceMobile
 *
 */

import React, { memo, useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import {
  flushStateAbsenceMobile,
  loadAbsencesMobile,
  changeAbsenceMobile,
  deleteAbsenceMobile,
} from "./actions";

import {
  selectAbsenceMobileLoading,
  selectAbsenceMobileError,
  selectAbsenceMobileDeleteSuccessful,
  selectAbsenceMobileData,
  selectAbsenceMobileList,
} from "./selectors";

// antd component
import { Layout, List, Button, Tabs } from "antd";

// assets
import {
  PlusOutlined,
  QuestionOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import CustomIcons from "components/CustomIcons";

// components
import SideListItem from "components/SideListItem";
import Preloader from "components/Preloader";

// mobile components
import InfoModal from "mobile/components/InfoModal";
import NoAbsence from "components/Absences/NoAbsence";
import CreateAbsence from "mobile/components/CreateAbsence";
import HomeDonatChart from "components/Charts/HomeDonatChart";

import { STATUSES_LIST_OPTIONS } from "utils/helpers/helper.constant";
import _local from "utils/localStorageUtils";

export function AbsenceMobile({
  // main data
  absenceList,
  absenceData,
  // states
  deleteSuccessful,
  error,
  loading,
  history,
  // actions
  loadAbsencesMobile,
  changeAbsenceMobile,
  flushState,
}) {
  useInjectReducer({ key: "absenceMobile", reducer });
  useInjectSaga({ key: "absenceMobile", saga });

  const { t, i18n } = useTranslation();

  // get from local
  const localUser = _local.getLocalUser();
  const contextSchool = _local.getLocalSchool();
  const contextChild = _local.getLocalChild();
  const { role } = localUser;

  const [createNew, setCreateNew] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);

  const [absenceOnEdit, setAbsenceOnEdit] = useState(false);

  const [showFinished, setShowFinished] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [finished, setFinished] = useState([]);
  const [dataNoProcessed, setDataNoProcessed] = useState([]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // --> main code here

    if (role == "parent" && contextChild?._id) {
      !absenceList && loadAbsencesMobile(role, contextChild?._id);
    } else if (role == "teacher") {
      !absenceList && loadAbsencesMobile(role, false);
    }

    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state after unmounting a component
      flushState();
      setAbsenceOnEdit(false);
      setShowFinished(false);
      setCreateNew(false);
    };
  }, []);

  useEffect(() => {
    if (role == "parent" && contextChild?._id) {
      loadAbsencesMobile(role, contextChild?._id);
    }
  }, [contextChild?._id]);

  useEffect(() => {
    if (role == "teacher" && contextSchool?._id) {
      loadAbsencesMobile(role, false); // set contextSchool?._id
    }
  }, [contextSchool?._id]);

  const absSort = (a, b) => {
    const moveSmaller = -1;
    const moveLarger = 1;
    if (a?.date_from && b?.date_from) {
      const dateA = new Date(a?.date_from);
      const dateB = new Date(a?.date_from);
      const dateASec = new Date(dateA).getTime() / 1000;
      const dateBSec = new Date(dateB).getTime() / 1000;
      if (dateASec > dateBSec) {
        return moveSmaller;
      }
      if (dateASec < dateBSec) {
        return moveLarger;
      }
    }
    return 0;
  };

  useEffect(() => {
    // TODO: change it on separate requests
    if (absenceList) {
      const finishedList = absenceList?.data
        .filter(abs => {
          if (
            ["accepted", "sickness-finish", "refused"]?.includes(abs?.status)
          ) {
            return abs;
          }
        })
        .sort(absSort);

      setFinished(finishedList);
      const otherList = absenceList?.data
        .filter(abs => !finishedList.includes(abs))
        .sort(absSort);
      const processedList = absenceList?.data
        .filter(abs => finishedList.includes(abs))
        .sort(absSort);
      setDataNoProcessed(otherList);
      setAbsences(processedList);
    }
  }, [loading, absenceList]);

  const changeAbsenceType = () => {
    setShowFinished(!showFinished);
  };

  const openHandler = useCallback(value => {
    setCreateNew(value);
    if (!value) {
      setAbsenceOnEdit(false);
    }
  }, []);

  const renderListImem = useCallback((item, index) => {
    return (
      <SideListItem
        key={`abs-list-item-${item?._id}-${index}`}
        type="absence"
        onClickAction={() => {
          setAbsenceOnEdit(item);
          openHandler(true);
        }}
        element={item}
      />
    );
  }, []);

  let absListOptions = {
    header: "123",
    itemLayout: "vertical",
    size: "large",
    loading: {
      spinning: loading,
      indicator: <Preloader />,
    },
    renderItem: renderListImem,
    locale: {
      emptyText: (
        <NoAbsence
          title={t("absences.noAbsencesToProcess")}
          content={t("absences.youProcessedAllAbsences")}
          newAction={() => setCreateNew(true)}
          filterAction={() => setShowFinished(true)}
        />
      ),
    },
  };

  const getAbsenceStatusIcon = useCallback(({ key, label }) => {
    switch (key) {
      case "pending":
        return (
          <div key={`status-icon-${key}`} className="status-item">
            <span className="status-tag status-tag--pending">
              <CustomIcons type="loop" />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case "sick":
        return (
          <div key={`status-icon-${key}`} className="status-item">
            <span className="status-tag status-tag--sick">
              <CustomIcons type="loop" />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case "justification-missing":
        return (
          <div key={`status-icon-${key}`} className="status-item">
            <span className="status-tag status-tag--justification-missing">
              <CustomIcons type="document" />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case "sickness-finish":
        return (
          <div key={`status-icon-${key}`} className="status-item">
            <span className="status-tag status-tag--sickness-finish">
              <CheckOutlined />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case "accepted":
        return (
          <div key={`status-icon-${key}`} className="status-item">
            <span className="status-tag status-tag--accepted">
              <CheckOutlined />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case "refused":
        return (
          <div key={`status-icon-${key}`} className="status-item">
            <span className="status-tag status-tag--refused">
              <CloseOutlined />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case "unjustified":
        return (
          <div key={`status-icon-${key}`} className="status-item">
            <span className="status-tag status-tag--unjustified">
              <QuestionOutlined />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
    }
  }, []);

  const AbsTabHeader = useCallback(
    ({ key }) => {
      const collapsed = false;
      return (
        <div className="absence-header">
          <Button
            key={`add-new-abs-${key}`}
            className={`absence-new-btn ${collapsed ? "collapsed-btn" : ""}`}
            onClick={() => openHandler(true)}
            disabled={
              (!!!contextChild?._id && localUser?.role == "parent") ||
              (!!!contextSchool?._id && localUser?.role == "teacher")
            }
          >
            <PlusOutlined />
            {!collapsed ? <span className="text">{t("absences.newAbsence")}</span> : ""}
          </Button>
          <Button
            key={`abs-info-abs-${key}`}
            className="absence-info-btn"
            onClick={() => setOpenInfo(true)}
          >
            <QuestionOutlined />
          </Button>
        </div>
      );
    },
    [contextChild?._id, contextSchool?._id, localUser?.role],
  );

  const processedAbsTab = useMemo(() => {
    return (
      <>
        {AbsTabHeader({
          key: "processed-abs",
        })}
        <List
          {...absListOptions}
          header={
            dataNoProcessed?.length > 0 && t("absences.processedAbsences")
          }
          key="processed-abs"
          className="absence-list processed"
          dataSource={dataNoProcessed}
        />
      </>
    );
  }, [absListOptions, dataNoProcessed, openHandler, i18n?.language]);

  const finishedAbsTab = useMemo(() => {
    return (
      <>
        {AbsTabHeader({
          key: "finished-abs",
        })}
        <List
          {...absListOptions}
          header={absences?.length > 0 && t("absences.listOfAbsences")}
          key="finished-abs"
          className="absence-list finished"
          dataSource={absences}
        />
      </>
    );
  }, [absListOptions, absences, openHandler, i18n?.language]);

  //const absChart = useMemo(() => <HomeDonatChart />, []);
  const getStatuses = useCallback(() => STATUSES_LIST_OPTIONS(), [i18n?.language]);

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.absence")}</title>
        <meta name="description" content="Description of Absence" />
      </Helmet>
      <Layout.Content className="main-content-container absence-page">
        <CreateAbsence
          key={`CreateAbsence-add-new`}
          absence={absenceOnEdit}
          isOpen={createNew}
          setOpen={openHandler}
          change={changeAbsenceMobile}
          hideButton={true}
        />
        <InfoModal
          key={`InfoModal-modal-new`}
          isOpen={openInfo}
          setOpen={setOpenInfo}
          hideButton={true}
          title={t("absences.modalWithInfoStatusTitle")}
        >
          <div className="statuses-list-info">
            {getStatuses()?.map(getAbsenceStatusIcon)}
          </div>
        </InfoModal>
        <Tabs
          defaultActiveKey="processed"
          className="absence-tabs"
          centered
          items={[
            {
              label: t("absences.processed"),
              key: "processed",
              children: processedAbsTab,
            },
            {
              label: t("absences.finished"),
              key: "finished",
              children: finishedAbsTab,
            },
            // {
            //   label: "Statistiques",
            //   key: "abschart",
            //   children: absChart,
            // },
          ]}
        />
      </Layout.Content>
    </Layout>
  );
}

AbsenceMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  deleteSuccessful: PropTypes.bool,
  loadAbsencesMobile: PropTypes.func.isRequired,
  changeAbsenceMobile: PropTypes.func,
  deleteAbsenceMobile: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  loading: selectAbsenceMobileLoading(),
  error: selectAbsenceMobileError(),
  deleteSuccessful: selectAbsenceMobileDeleteSuccessful(),
  absenceList: selectAbsenceMobileList(),
  absenceData: selectAbsenceMobileData(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateAbsenceMobile()),
    loadAbsencesMobile: (role, child) =>
      dispatch(loadAbsencesMobile(role, child)),
    changeAbsenceMobile: data => dispatch(changeAbsenceMobile(data)),
    deleteAbsenceMobile: id => dispatch(deleteAbsenceMobile(id)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(AbsenceMobile);
